import type { QueryClient } from "@tanstack/react-query";
import type { MutableRefObject } from "react";
import { toast } from "@volca/ui/lib/toast";
import { getCellKind } from "@/payroll/ai-script/cell-kind-utils";
import { aiScriptSpreadsheetSheetKey } from "@/payroll/ai-script/query";
import {
  findSheetResponseCell,
  patchSpreadsheetSheetCells,
} from "@/payroll/ai-script/spreadsheet-cache";
import {
  buildPendingManualRowCell,
  markFailedManualRowEdits,
  restoreManualRowBaselines,
} from "@/payroll/ai-script/spreadsheet-cache/pending-manual-rows";
import type {
  CachedSpreadsheetCell,
  CachedSpreadsheetSheetResponse,
  IAiSpreadsheetPasteEdit,
  IAiSpreadsheetSheet,
} from "@/payroll/ai-script/types";
import type { MutationErrorHandlers } from "./mutation-error-feedback";
import {
  type BufferedManualRowEdit,
  isOptimisticManualCell,
  optimisticManualCellBelongsTo,
  resolveBufferedManualRowEdits,
} from "./optimistic-manual-row-edits";
import type { ViewerMutations } from "./use-viewer-mutations";
import type { UseWorkbookHistoryResult } from "./use-workbook-history";
import { buildHistoryCellEdits } from "./viewer-helpers";
import { buildPasteEntry } from "./workbook-history-ops";

export type BufferedManualRowEdits = Map<
  string,
  { edits: Map<number, BufferedManualRowEdit>; saving: boolean }
>;

export interface ViewerEditContext {
  businessId: string | null;
  runId: string;
  queryClient: QueryClient;
  history: UseWorkbookHistoryResult;
  mutations: ViewerMutations;
  activeSheet: IAiSpreadsheetSheet | undefined;
  bufferedManualRowEditsRef: MutableRefObject<BufferedManualRowEdits>;
  recordWorkbookVersion: (version: number | undefined) => void;
  handleMutationError: MutationErrorHandlers["handleMutationError"];
}

export const createManualRowEditBuffer = ({
  businessId,
  runId,
  queryClient,
  history,
  mutations: { bulkPasteAsync },
  activeSheet,
  bufferedManualRowEditsRef,
  recordWorkbookVersion,
  handleMutationError,
}: ViewerEditContext) => {
  const bufferManualRowEdit = (
    cell: CachedSpreadsheetCell,
    edit: Omit<IAiSpreadsheetPasteEdit, "cell_id">,
  ): boolean => {
    if (!activeSheet) return false;

    const token = Array.from(bufferedManualRowEditsRef.current.keys()).find(
      (candidate) =>
        !bufferedManualRowEditsRef.current.get(candidate)?.saving &&
        optimisticManualCellBelongsTo(cell, candidate),
    );
    if (!token) return false;

    bufferedManualRowEditsRef.current
      .get(token)
      ?.edits.set(cell.col, { col: cell.col, ...edit });

    const sheetKey = aiScriptSpreadsheetSheetKey(
      businessId,
      runId,
      activeSheet.id,
    );
    queryClient.setQueryData<CachedSpreadsheetSheetResponse>(sheetKey, (data) =>
      patchSpreadsheetSheetCells(data, [buildPendingManualRowCell(cell, edit)]),
    );
    return true;
  };

  const bufferOptimisticPasteEdits = (
    edits: IAiSpreadsheetPasteEdit[],
  ): IAiSpreadsheetPasteEdit[] => {
    if (!activeSheet) return edits;

    const sheetData = queryClient.getQueryData<CachedSpreadsheetSheetResponse>(
      aiScriptSpreadsheetSheetKey(businessId, runId, activeSheet.id),
    );

    return edits.filter((edit) => {
      const cell = findSheetResponseCell(sheetData, edit.cell_id);
      if (!cell) return true;

      const { cell_id: _cellId, ...bufferedEdit } = edit;
      return !bufferManualRowEdit(cell, bufferedEdit);
    });
  };

  const flushBufferedManualRowEdits = async ({
    token,
    sheetId,
    manualRowId,
  }: {
    token: string;
    sheetId: string;
    manualRowId: string | undefined;
  }) => {
    const sheetKey = aiScriptSpreadsheetSheetKey(businessId, runId, sheetId);
    const abandon = () => {
      const buffer = bufferedManualRowEditsRef.current.get(token);
      bufferedManualRowEditsRef.current.delete(token);
      if (!buffer?.edits.size) return false;
      queryClient.setQueryData<CachedSpreadsheetSheetResponse>(
        sheetKey,
        (data) => markFailedManualRowEdits(data, token),
      );
      return true;
    };
    const failLookup = (description: string) => {
      if (!abandon()) return;
      toast.error("Couldn't save edits to the new row", { description });
    };
    if (!manualRowId) {
      failLookup(
        "The row was added, but its pending cell edits could not be matched. Please try them again.",
      );
      void queryClient.invalidateQueries({ queryKey: sheetKey });
      return;
    }

    let sheetData =
      queryClient.getQueryData<CachedSpreadsheetSheetResponse>(sheetKey);
    if (
      !sheetData?.sheet.cells.some((cell) => cell.manual_row_id === manualRowId)
    ) {
      await queryClient.refetchQueries({ queryKey: sheetKey, type: "all" });
      sheetData =
        queryClient.getQueryData<CachedSpreadsheetSheetResponse>(sheetKey);
    }
    if (
      !sheetData?.sheet.cells.some((cell) => cell.manual_row_id === manualRowId)
    ) {
      failLookup(
        "The row was added, but its pending cell edits could not be matched. Please try them again.",
      );
      return;
    }

    const buffer = bufferedManualRowEditsRef.current.get(token);
    if (!buffer || buffer.saving) return;
    const buffered = Array.from(buffer.edits.values());
    if (buffered.length === 0) {
      bufferedManualRowEditsRef.current.delete(token);
      return;
    }

    const edits = resolveBufferedManualRowEdits({
      cells: sheetData.sheet.cells,
      manualRowId,
      edits: buffered,
    });
    if (edits.length !== buffered.length) {
      failLookup(
        "The row was added, but one or more pending cell edits could not be matched. Please try them again.",
      );
      void queryClient.invalidateQueries({ queryKey: sheetKey });
      return;
    }

    const cellEdits = buildHistoryCellEdits(
      restoreManualRowBaselines(sheetData),
      edits,
    );
    buffer.saving = true;
    try {
      const response = await bulkPasteAsync({
        runId,
        sheetId,
        edits,
        rows: [],
        flushPendingCellEdits: false,
      });
      recordWorkbookVersion(response.version);
      bufferedManualRowEditsRef.current.delete(token);
      history.push(
        buildPasteEntry({ sheetId, cellEdits, label: "Edit new row" }),
      );
    } catch (mutationError) {
      abandon();
      handleMutationError("Failed to update the new row", mutationError);
    }
  };

  const retryFailedManualRowEdits = async () => {
    if (!activeSheet) return;
    const sheetKey = aiScriptSpreadsheetSheetKey(
      businessId,
      runId,
      activeSheet.id,
    );
    let sheetData =
      queryClient.getQueryData<CachedSpreadsheetSheetResponse>(sheetKey);
    if (
      sheetData?.sheet.cells.some(
        (cell) => cell.manual_row_save_failed && isOptimisticManualCell(cell),
      )
    ) {
      await queryClient.refetchQueries({ queryKey: sheetKey, type: "all" });
      sheetData =
        queryClient.getQueryData<CachedSpreadsheetSheetResponse>(sheetKey);
    }
    if (!sheetData) return;
    if (
      sheetData.sheet.cells.some(
        (cell) => cell.manual_row_save_failed && isOptimisticManualCell(cell),
      )
    ) {
      toast.error("Couldn't load the saved row", {
        description: "Retry when the sheet is available.",
      });
      return;
    }
    const edits = sheetData.sheet.cells
      .filter(
        (cell) => cell.manual_row_save_failed && !isOptimisticManualCell(cell),
      )
      .map((cell) => ({
        cell_id: cell.id,
        raw_input: cell.raw_input ?? "",
        kind: getCellKind(cell),
        value_type: cell.pending_manual_row_value_type,
      }));
    if (edits.length === 0) return;
    const cellEdits = buildHistoryCellEdits(
      restoreManualRowBaselines(sheetData),
      edits,
    );
    try {
      const response = await bulkPasteAsync({
        runId,
        sheetId: activeSheet.id,
        edits,
        rows: [],
      });
      recordWorkbookVersion(response.version);
      history.push(
        buildPasteEntry({
          sheetId: activeSheet.id,
          cellEdits,
          label: "Retry unsaved edits",
        }),
      );
    } catch (mutationError) {
      handleMutationError("Failed to save edits", mutationError);
    }
  };

  return {
    bufferManualRowEdit,
    bufferOptimisticPasteEdits,
    flushBufferedManualRowEdits,
    retryFailedManualRowEdits,
  };
};

export type ManualRowEditBuffer = ReturnType<typeof createManualRowEditBuffer>;
